import { FieldArray, FormikProps } from "formik";
import ReactDragListView from 'react-drag-listview';
import { AiOutlinePlus } from "react-icons/ai";
import { MdDelete } from "react-icons/md";
import { TbGrid3X3 } from 'react-icons/tb';
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMRadioButton from "src/components/UI/atoms/ATMRadioButton/ATMRadioButton";
import ATMHTMLEditor from "src/components/UI/atoms/formFields/ATMHTMLEditor/ATMHTMLEditor";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import { WebCourseFormValues } from "src/models/WebCourse.model";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { MdExpandMore } from "react-icons/md";
import MOLVideoPlayer from "src/components/UI/molecules/MOLVideoPlayer/MOLVideoPlayer";
import { getVideoInfo } from "src/utils/getVideoInfo/getVideoInfo";
import { handleValidNumber } from "src/utils/validations/numberInput";

type Props = {
    formikProps: FormikProps<WebCourseFormValues>;
};

const demoType = [
    { label: "VIDEO", value: "VIDEO" },
    { label: "PDF", value: "PDF" },
];

const videoType = [
    {
        label: "YOUTUBE",
        value: "YOUTUBE",
    },

    {
        label: "VIMEO",
        value: "VIMEO",
    },
];

const CourseContentForm = ({ formikProps }: Props) => {
    const { values, setFieldValue, handleBlur, isSubmitting } = formikProps;

    const dragProps = {
        onDragEnd(fromIndex: number, toIndex: number) {
            const newData = [...values?.courseModule];
            const [movedItem] = newData.splice(fromIndex, 1);
            newData.splice(toIndex, 0, movedItem);
            setFieldValue("courseModule", newData);
        },
        nodeSelector: '.timeline-item',
        handleSelector: '.drag-handle',
    };

    const NavItem: React.FC<any> = ({ item, level = 0 }) => {
        return (
            <div className={`${level === 0 ? "" : "pl-3 w-full border-2-r border-black"}`}>
                <ReactDragListView {...dragProps}>
                    <div className="flex flex-col gap-2 text-sm">
                        {item?.map((chapter: any, index: number) => (
                            <div className='flex flex-col timeline-item drag-handle' key={index}>
                                <div className='flex items-center gap-2'>
                                    <TbGrid3X3 />
                                    <span className='capitalize'>
                                        {chapter?.moduleName ? chapter?.moduleName : `Module ${index + 1}`}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </ReactDragListView>
                <p
                    className="mt-2 text-link font-semibold text-base text-[16px] max-[1024px]:text-sm flex items-center cursor-pointer"
                    onClick={() =>
                        setFieldValue('courseModule', [
                            ...item,
                            {
                                moduleName: "",
                                lession: [
                                    {
                                        lessionName: "",
                                        lessionDescription: "",
                                        documentType: "",
                                        documentLink: "",
                                    }
                                ],
                            }
                        ])
                    }
                >
                    <AiOutlinePlus size={18} color="#0064C4" className="mr-2" />
                    Add Module
                </p>
            </div>
        );
    };

    return (
        <div className="w-full mx-auto px-6 py-4 h-full">
            <div className='grid grid-cols-12 h-[70vh]'>
                <div className='col-span-2'>
                    <NavItem key={1} item={values?.courseModule} />
                </div>
                <div className='col-span-10 overflow-y-auto h-full overflow-x-hidden'>
                    <FieldArray
                        name="courseModule"
                        render={({ push, remove }) => (
                            <>
                                {values?.courseModule?.map((module: any, moduleIndex: number) => (
                                    <Accordion
                                        className="border mb-4"
                                        key={moduleIndex}>
                                        <AccordionSummary
                                            expandIcon={<MdExpandMore />}
                                            aria-controls={`panel${moduleIndex}-content`}
                                            id={`panel${moduleIndex}-header`}
                                        >
                                            <Typography>Module {moduleIndex + 1}: {module.moduleName}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <div className="flex justify-end mt-3">
                                                <MdDelete
                                                    color="#F70913"
                                                    size={30}
                                                    className="pl-1 cursor-pointer"
                                                    onClick={() => {
                                                        showConfirmationDialog({
                                                            title: "Heads Up",
                                                            text: "Are you sure want to Delete this Module?",
                                                            icon: "question",
                                                            showCancelButton: true,
                                                            next: (result) => {
                                                                if (result.isConfirmed) {
                                                                    remove(moduleIndex);
                                                                }
                                                            },
                                                        });
                                                    }}
                                                />
                                            </div>
                                            <ATMTextField
                                                name={`courseModule[${moduleIndex}].moduleName`}
                                                value={module.moduleName}
                                                onChange={(e) => setFieldValue(`courseModule[${moduleIndex}].moduleName`, e.target.value)}
                                                label={`Module Name ${moduleIndex + 1}`}
                                                placeholder="Enter Module Name"
                                                onBlur={handleBlur}
                                            />
                                            <FieldArray
                                                name={`courseModule[${moduleIndex}].lession`}
                                                render={({ push, remove }) => (
                                                    <>
                                                        {module?.lession?.map((lesson: any, lessonIndex: number) => (
                                                            <Accordion key={lessonIndex}>
                                                                <AccordionSummary
                                                                    expandIcon={<MdExpandMore />}
                                                                    aria-controls={`lesson-panel${lessonIndex}-content`}
                                                                    id={`lesson-panel${lessonIndex}-header`}
                                                                    className="rounded"
                                                                >
                                                                    <Typography>Lesson {lessonIndex + 1}: {lesson.lessionName}</Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails>
                                                                    <div className="flex justify-end mt-3">
                                                                        <MdDelete
                                                                            color="#F70913"
                                                                            size={30}
                                                                            className="pl-1 cursor-pointer"
                                                                            onClick={() => {
                                                                                showConfirmationDialog({
                                                                                    title: "Heads Up",
                                                                                    text: "Are you sure want to Delete this Lesson?",
                                                                                    icon: "question",
                                                                                    showCancelButton: true,
                                                                                    next: (result) => {
                                                                                        if (result.isConfirmed) {
                                                                                            remove(lessonIndex);
                                                                                        }
                                                                                    },
                                                                                });
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <ATMTextField
                                                                        required
                                                                        name={`courseModule[${moduleIndex}].lession[${lessonIndex}].lessionName`}
                                                                        value={lesson.lessionName}
                                                                        onChange={(e) => setFieldValue(`courseModule[${moduleIndex}].lession[${lessonIndex}].lessionName`, e.target.value)}
                                                                        label={`Lesson Name ${lessonIndex + 1}`}
                                                                        placeholder="Enter Lesson Name"
                                                                        onBlur={handleBlur}
                                                                    />

                                                                    <div className="mt-4">
                                                                        <ATMHTMLEditor
                                                                            required
                                                                            name={`courseModule[${moduleIndex}].lession[${lessonIndex}].lessionDescription`}
                                                                            label="Lesson Description"
                                                                            value={lesson.lessionDescription}
                                                                            placeholder="Write here...."
                                                                            onChange={(newValue) => setFieldValue(`courseModule[${moduleIndex}].lession[${lessonIndex}].lessionDescription`, newValue)}
                                                                        />
                                                                    </div>
                                                                    <div className="grid grid-cols-3 gap-4 mt-4">
                                                                        <div className="">
                                                                            <ATMRadioButton
                                                                                required
                                                                                label="Document Type"
                                                                                name={`courseModule[${moduleIndex}].lession[${lessonIndex}].documentType`}
                                                                                value={lesson.documentType}
                                                                                className="mt-0 flex gap-6"
                                                                                options={demoType}
                                                                                onChange={(e: any) => {
                                                                                    setFieldValue(`courseModule[${moduleIndex}].lession[${lessonIndex}].documentType`, e);
                                                                                    setFieldValue(`courseModule[${moduleIndex}].lession[${lessonIndex}].videoType`, '');
                                                                                    setFieldValue(`courseModule[${moduleIndex}].lession[${lessonIndex}].documentLink`, '')
                                                                                }}
                                                                            />
                                                                        </div>
                                                                        {lesson?.documentType === 'VIDEO' &&
                                                                            <div className="z-10">
                                                                                <ATMRadioButton
                                                                                    required
                                                                                    label="Video Type"
                                                                                    name={`courseModule[${moduleIndex}].lession[${lessonIndex}].videoType`}
                                                                                    value={lesson?.videoType || ""}
                                                                                    className="mt-0 flex gap-6"
                                                                                    options={videoType}
                                                                                    onChange={(e) => {
                                                                                        setFieldValue(`courseModule[${moduleIndex}].lession[${lessonIndex}].videoType`, e);
                                                                                        setFieldValue(`courseModule[${moduleIndex}].lession[${lessonIndex}].documentLink`, '')
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        }
                                                                        <div className="">
                                                                            <ATMTextField
                                                                                required
                                                                                name={`courseModule[${moduleIndex}].lession[${lessonIndex}].lessonDuration`}
                                                                                value={lesson.lessonDuration}
                                                                                onChange={(e) => handleValidNumber(e) && setFieldValue(`courseModule[${moduleIndex}].lession[${lessonIndex}].lessonDuration`, e.target.value)}
                                                                                label={`Lesson Duration (mint)`}
                                                                                placeholder="Enter Lesson Duration"
                                                                                onBlur={handleBlur}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="mt-2 mb-2">
                                                                        <ATMTextField
                                                                            name={`courseModule[${moduleIndex}].lession[${lessonIndex}].documentLink`}
                                                                            value={lesson.documentLink}
                                                                            onChange={(e) => setFieldValue(`courseModule[${moduleIndex}].lession[${lessonIndex}].documentLink`, e.target.value)}
                                                                            label="Video URL"
                                                                            placeholder="Enter video URL"
                                                                            onBlur={handleBlur}
                                                                        />
                                                                        {lesson?.documentLink && (
                                                                            <div className="w-[550px] my-2">
                                                                                <MOLVideoPlayer
                                                                                    videoType={lesson?.documentType === 'PDF' ? "UPLOADED" : lesson?.videoType}
                                                                                    videoId={getVideoInfo(lesson.documentLink)?.videoId}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        ))}
                                                        <div className="flex mt-1 justify-end">
                                                            <p
                                                                className="text-link font-semibold text-base text-[16px] max-[1024px]:text-sm flex items-center cursor-pointer"
                                                                onClick={() =>
                                                                    push({
                                                                        lessionName: "",
                                                                        lessionDescription: "",
                                                                        documentType: "",
                                                                        documentLink: "",
                                                                    })
                                                                }
                                                            >
                                                                <AiOutlinePlus size={18} color="#0064C4" className="mr-2" />
                                                                Add Lesson
                                                            </p>
                                                        </div>
                                                    </>
                                                )}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                                <div className="flex mt-1 justify-end">
                                    <p
                                        className="text-link font-semibold text-base text-[16px] max-[1024px]:text-sm flex items-center cursor-pointer"
                                        onClick={() =>
                                            push({
                                                moduleName: "",
                                                lession: [
                                                    {
                                                        lessionName: "",
                                                        lessionDescription: "",
                                                        documentType: "",
                                                        documentLink: "",
                                                    }
                                                ],
                                            })
                                        }
                                    >
                                        <AiOutlinePlus size={18} color="#0064C4" className="mr-2" />
                                        Add Module
                                    </p>
                                </div>
                            </>
                        )}
                    />
                </div>
            </div>
            <div className="flex justify-end">
                <ATMLoadingButton
                    className="w-[300px]"
                    isLoading={isSubmitting} type="submit">
                    Save
                </ATMLoadingButton>
            </div>
        </div>
    );
};

export default CourseContentForm;
